<script>
import { mapGetters } from 'vuex';

export default {
    name: 'PartnerAggregateFunnel',

    props: {
        partnerId: {
            type: Number,
            required: true,
        },
    },

    computed: {
        ...mapGetters(['AGGREGATE_FUNNEL', 'IS_LINKS_LOADING']),

        funnel() {
            return this.AGGREGATE_FUNNEL;
        },

        trafficCount() {
            if (!this.funnel) return 0;
            const t = this.funnel.steps.find((s) => s.name === 'traffic');
            return t ? t.count : 0;
        },

        displayedSteps() {
            if (!this.funnel) return [];
            return this.funnel.steps.map((step) => {
                const pct = this.trafficCount > 0 ? Math.round((step.count / this.trafficCount) * 100) : null;
                return { ...step, pct };
            });
        },

        bottleneckStep() {
            if (!this.funnel || this.funnel.steps.length < 2) return null;
            const steps = this.funnel.steps;
            let maxLost = 0;
            let result = null;
            steps.forEach((s, i) => {
                if (i === 0) return;
                const lost = steps[i - 1].count - s.count;
                if (lost > maxLost) {
                    maxLost = lost;
                    const trafficN = this.trafficCount;
                    // Compute "1 in N" ratio relative to traffic
                    const ratio = trafficN > 0 ? Math.round(trafficN / Math.max(s.count, 1)) : null;
                    result = { name: s.name, label: this.stepLabel(s.name), ratio };
                }
            });
            if (result) {
                console.debug(`[PartnerAggregateFunnel] rendering ${steps.length} steps, bottleneck=${result.name} ratio=1in${result.ratio}`);
            }
            return result;
        },
    },

    methods: {
        stepLabel(name) {
            const labels = {
                traffic: 'Traffic',
                visit: 'Site Visit',
                signup: 'Registration',
                first_action: 'First Action',
                core_action: 'Download',
                retention_7d: 'Came Back (7d)',
                retention_30d: 'Came Back (30d)',
            };
            return labels[name] || name;
        },

        stepIcon(name) {
            const icons = {
                traffic: '🔗',
                visit: '👁',
                signup: '✏️',
                first_action: '⚡',
                core_action: '🎯',
                retention_7d: '📅',
                retention_30d: '🔄',
            };
            return icons[name] || '•';
        },

        stepHint(name) {
            const hints = {
                traffic: 'Clicks on the partner referral link (?ref=…). Counted once per click, including repeat visits from the same user.',
                visit: 'Unique visitors: deduplicated by IP per day. Reflects true reach — multiple clicks from the same person count as one.',
                signup: 'Users who completed registration after clicking the link. Attribution window: 30 days from first click.',
                first_action: 'Registered users who performed any action (search, download, save) at least once after signing up.',
                core_action: 'Users who downloaded at least one asset — the primary conversion goal.',
                retention_7d: 'Registered users who returned and were active at least once within 7 days of their signup date.',
                retention_30d: 'Registered users who returned and were active at least once within 30 days of their signup date.',
            };
            return hints[name] || '';
        },

        barWidth(count) {
            if (!this.trafficCount) return 0;
            return Math.round((count / this.trafficCount) * 100);
        },

        barHealthClass(index) {
            if (index === 0) return 'bar-neutral';
            if (!this.funnel) return 'bar-neutral';
            const pct = this.displayedSteps[index]?.pct ?? 100;
            if (pct >= 50) return 'bar-green';
            if (pct >= 25) return 'bar-amber';
            return 'bar-red';
        },

        conversionClass(pct) {
            if (pct >= 50) return 'pct-green';
            if (pct >= 25) return 'pct-yellow';
            return 'pct-red';
        },
    },
};
</script>

<template>
    <div class="agg-funnel">
        <div class="agg-funnel__header">
            <span class="agg-funnel__title">PARTNER FUNNEL</span>
            <span v-if="bottleneckStep" class="agg-funnel__bottleneck">
                ⚡ Bottleneck: {{ bottleneckStep.label }}
                <template v-if="bottleneckStep.ratio"> — only 1 in {{ bottleneckStep.ratio }}</template>
            </span>
        </div>

        <div v-if="IS_LINKS_LOADING" class="agg-funnel__state">Loading…</div>
        <div v-else-if="!funnel" class="agg-funnel__state">No funnel data yet. Open a link's funnel first.</div>

        <table v-else class="agg-funnel__table">
            <tbody>
                <tr
                    v-for="(step, index) in displayedSteps"
                    :key="step.name"
                    :class="{ 'row-worst': bottleneckStep && step.name === bottleneckStep.name }"
                >
                    <td class="step-cell">
                        <span class="step-icon">{{ stepIcon(step.name) }}</span>
                        <span class="step-hint-wrap">
                            {{ stepLabel(step.name) }}
                            <span class="step-hint-bubble">{{ stepHint(step.name) }}</span>
                        </span>
                    </td>
                    <td class="count-cell">{{ step.count.toLocaleString() }}</td>
                    <td class="pct-cell">
                        <span v-if="step.pct !== null && index > 0" :class="['pct-badge', conversionClass(step.pct)]">
                            {{ step.pct }}%
                        </span>
                        <span v-else class="pct-badge pct-none">—</span>
                    </td>
                    <td class="bar-cell">
                        <div class="funnel-bar">
                            <div
                                :class="['funnel-bar__fill', barHealthClass(index)]"
                                :style="{ width: `${barWidth(step.count)}%` }"
                            />
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<style scoped>
.agg-funnel {
    margin: 0 0 16px;
    padding: 12px 14px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
}

.agg-funnel__header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
    flex-wrap: wrap;
}

.agg-funnel__title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: #64748b;
    text-transform: uppercase;
}

.agg-funnel__bottleneck {
    font-size: 12px;
    font-weight: 600;
    color: #dc2626;
    background: #fee2e2;
    border-radius: 4px;
    padding: 2px 8px;
}

.agg-funnel__state {
    font-size: 12px;
    color: #94a3b8;
    padding: 4px 0;
}

.agg-funnel__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.agg-funnel__table td {
    padding: 5px 8px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
}

.agg-funnel__table tr:last-child td {
    border-bottom: none;
}

.agg-funnel__table tr.row-worst {
    background: rgba(220, 38, 38, 0.04);
    border-left: 3px solid #ef4444;
}

.step-cell {
    white-space: nowrap;
    min-width: 140px;
}

.step-icon {
    margin-right: 5px;
    font-size: 13px;
}

.count-cell {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    text-align: right;
    padding-right: 12px;
    min-width: 56px;
    white-space: nowrap;
}

.pct-cell {
    min-width: 52px;
    text-align: right;
    padding-right: 12px;
}

.pct-badge {
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 10px;
    font-weight: 600;
    white-space: nowrap;
}
.pct-green  { background: #dcfce7; color: #166534; }
.pct-yellow { background: #fef9c3; color: #854d0e; }
.pct-red    { background: #fee2e2; color: #991b1b; }
.pct-none   { background: #f3f4f6; color: #6b7280; }

.bar-cell {
    width: 160px;
}

.funnel-bar {
    height: 8px;
    background: #e5e7eb;
    border-radius: 4px;
    overflow: hidden;
}

.funnel-bar__fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.3s ease;
}

.bar-neutral { background: #38bdf8; }
.bar-green   { background: #22c55e; }
.bar-amber   { background: #f59e0b; }
.bar-red     { background: #ef4444; }

/* ---- Step tooltips (same pattern as PartnerFunnel.vue) ---- */
.step-hint-wrap {
    position: relative;
    cursor: default;
    border-bottom: 1px dotted #94a3b8;
    display: inline;
}
.step-hint-bubble {
    position: absolute;
    bottom: calc(100% + 6px);
    left: 0;
    z-index: 100;
    width: 240px;
    background: #1e293b;
    color: #f1f5f9;
    font-size: 11px;
    line-height: 1.5;
    padding: 6px 8px;
    border-radius: 6px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s ease;
    white-space: normal;
    font-weight: 400;
}
.step-hint-bubble::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 12px;
    border: 5px solid transparent;
    border-top-color: #1e293b;
}
.step-hint-wrap:hover .step-hint-bubble {
    opacity: 1;
}
</style>
